import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Clock, ExternalLink, Copy, CheckCircle, AlertTriangle, X, Play, Sparkles } from 'lucide-react';

// Get API URL from window origin to ensure correct domain
const API_URL = window.location.origin;

// Demo Context - provides demo state to all child components
export const DemoContext = createContext(null);

export const useDemoMode = () => {
  const context = useContext(DemoContext);
  return context || { isDemoMode: false, demoData: null, demoUser: null };
};

// Demo Mode Provider - wraps the app when in demo mode
export const DemoModeProvider = ({ children, demoSession }) => {
  const [isDemoMode] = useState(true);
  const [demoData, setDemoData] = useState(demoSession?.demo_data || {});
  const [demoUser] = useState(demoSession?.demo_user || null);
  const [allowedPages] = useState(demoSession?.allowed_pages || []);
  const [template] = useState(demoSession?.template || {});
  
  const value = {
    isDemoMode,
    demoData,
    setDemoData,
    demoUser,
    allowedPages,
    template,
    sessionId: demoSession?.session_id,
    expiresAt: demoSession?.expires_at
  };
  
  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

// Demo Banner - shows at top of page in demo mode
export const DemoBanner = ({ template, expiresAt, onExit }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    const updateTimeLeft = () => {
      if (!expiresAt) return;
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimeLeft('Expired');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${hours}h ${minutes}m`);
    };
    
    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 60000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  const handleCopyLink = () => {
    const demoUrl = `${window.location.origin}/demo/${template?.id}`;
    navigator.clipboard.writeText(demoUrl);
    setCopied(true);
    toast.success('Demo link copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] px-4 py-2 flex items-center justify-between gap-4"
      style={{ 
        backgroundColor: template?.color || '#6B7280',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
      }}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-black/20 px-3 py-1 rounded-full">
          <Eye size={14} className="text-white" />
          <span className="text-white text-xs font-bold uppercase tracking-wider">Demo Mode</span>
        </div>
        <span className="text-white/90 text-sm font-medium hidden sm:block">
          {template?.name} • View Only
        </span>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-white/80 text-xs hidden md:flex">
          <Clock size={12} />
          <span>Expires in {timeLeft}</span>
        </div>
        
        <button
          onClick={handleCopyLink}
          className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-full transition-colors"
        >
          {copied ? <CheckCircle size={12} /> : <Copy size={12} />}
          <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy Link'}</span>
        </button>
        
        <button
          onClick={onExit}
          className="flex items-center gap-1.5 px-3 py-1 bg-black/30 hover:bg-black/40 text-white text-xs font-medium rounded-full transition-colors"
        >
          <X size={12} />
          <span className="hidden sm:inline">Exit Demo</span>
        </button>
      </div>
    </div>
  );
};

// View Only Overlay - shows when user tries to edit in demo mode
export const ViewOnlyOverlay = ({ show, onClose }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-yellow-500/30 rounded-xl p-6 max-w-md text-center" onClick={e => e.stopPropagation()}>
        <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={24} className="text-yellow-400" />
        </div>
        <h3 className="text-white text-lg font-bold mb-2">View Only Mode</h3>
        <p className="text-gray-400 text-sm mb-4">
          This is a demo preview. Editing is disabled to preserve the demo experience.
          Sign up for a free account to create and manage your own data!
        </p>
        <div className="flex gap-3 justify-center">
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm">
            Continue Viewing
          </button>
          <a 
            href="/auth" 
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg text-sm font-medium"
          >
            Sign Up Free
          </a>
        </div>
      </div>
    </div>
  );
};

// Demo Landing Page - shown before entering demo
const DemoLandingPage = ({ template, onStartDemo, loading }) => {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${template?.color}20` }}
          >
            <Sparkles size={36} style={{ color: template?.color }} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{template?.name} Demo</h1>
          <p className="text-gray-400">{template?.description}</p>
        </div>
        
        {/* Features */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
          <h3 className="text-white font-semibold mb-4">What you'll see:</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3 text-gray-300">
              <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span>Realistic mockup data tailored for {template?.name}</span>
            </li>
            <li className="flex items-start gap-3 text-gray-300">
              <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span>All features and pages relevant to your industry</span>
            </li>
            <li className="flex items-start gap-3 text-gray-300">
              <CheckCircle size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span>24-hour session persistence</span>
            </li>
            <li className="flex items-start gap-3 text-gray-300">
              <Eye size={16} className="text-yellow-400 mt-0.5 flex-shrink-0" />
              <span>View-only mode (no editing)</span>
            </li>
          </ul>
        </div>
        
        {/* Start Button */}
        <button
          onClick={onStartDemo}
          disabled={loading}
          className="w-full py-4 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-3 transition-all hover:scale-[1.02]"
          style={{ 
            background: `linear-gradient(135deg, ${template?.color}, ${template?.color}90)`,
            boxShadow: `0 4px 20px ${template?.color}40`
          }}
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Preparing Demo...
            </>
          ) : (
            <>
              <Play size={20} fill="white" />
              Start Live Demo
            </>
          )}
        </button>
        
        <p className="text-center text-gray-500 text-xs mt-4">
          No account required • Demo expires in 24 hours
        </p>
      </div>
    </div>
  );
};

// Main Demo Page Component
const DemoPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [template, setTemplate] = useState(null);
  const [demoSession, setDemoSession] = useState(null);
  const [error, setError] = useState(null);
  const [showLanding, setShowLanding] = useState(true);
  
  // Check for existing session or fetch template info
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Check localStorage for existing session
        const storedSessionId = localStorage.getItem(`demo_session_${templateId}`);
        
        if (storedSessionId) {
          // Try to restore existing session
          const response = await fetch(`${API_URL}/api/demo/session/${storedSessionId}`);
          if (response.ok) {
            const data = await response.json();
            setDemoSession(data);
            setTemplate(data.template);
            setShowLanding(false);
            setLoading(false);
            return;
          } else {
            // Session expired, clear it
            localStorage.removeItem(`demo_session_${templateId}`);
          }
        }
        
        // Fetch template info for landing page
        const templatesResponse = await fetch(`${API_URL}/api/demo/templates`);
        if (templatesResponse.ok) {
          const data = await templatesResponse.json();
          const foundTemplate = data.templates?.find(t => 
            t.id === templateId || 
            t.id.toLowerCase() === templateId.toLowerCase()
          );
          
          if (foundTemplate) {
            setTemplate(foundTemplate);
          } else {
            setError(`Template "${templateId}" not found`);
          }
        }
      } catch (err) {
        console.error('Demo session check error:', err);
        setError('Failed to load demo');
      } finally {
        setLoading(false);
      }
    };
    
    checkSession();
  }, [templateId]);
  
  // Initialize demo session
  const handleStartDemo = async () => {
    setInitializing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/demo/init/${templateId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to initialize demo');
      }
      
      const data = await response.json();
      
      // Store session ID for persistence
      localStorage.setItem(`demo_session_${templateId}`, data.session_id);
      
      // Fetch full session data
      const sessionResponse = await fetch(`${API_URL}/api/demo/session/${data.session_id}`);
      const sessionData = await sessionResponse.json();
      
      setDemoSession(sessionData);
      setTemplate(sessionData.template);
      setShowLanding(false);
      
      toast.success(`Welcome to ${sessionData.template?.name} demo!`);
      
    } catch (err) {
      console.error('Demo init error:', err);
      toast.error(err.message || 'Failed to start demo');
      setError(err.message);
    } finally {
      setInitializing(false);
    }
  };
  
  // Exit demo
  const handleExitDemo = () => {
    localStorage.removeItem(`demo_session_${templateId}`);
    navigate('/');
    toast.info('Exited demo mode');
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading demo...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={32} className="text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Demo Error</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/admin')}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
          >
            Back to Admin
          </button>
        </div>
      </div>
    );
  }
  
  // Landing page
  if (showLanding && template) {
    return (
      <DemoLandingPage 
        template={template}
        onStartDemo={handleStartDemo}
        loading={initializing}
      />
    );
  }
  
  // Demo mode active - set up auth and redirect to dashboard
  if (demoSession) {
    // Store demo session in sessionStorage for access across pages
    sessionStorage.setItem('activeDemo', JSON.stringify({
      sessionId: demoSession.session_id,
      template: demoSession.template,
      demoUser: demoSession.demo_user,
      demoData: demoSession.demo_data,
      allowedPages: demoSession.allowed_pages,
      expiresAt: demoSession.expires_at
    }));
    
    // Set up fake auth token and user for demo mode to bypass ProtectedRoute
    // Create a demo token (this is just for bypassing frontend auth check)
    const demoToken = `demo_token_${demoSession.session_id}`;
    localStorage.setItem('auth_token', demoToken);
    localStorage.setItem('auth_user', JSON.stringify({
      ...demoSession.demo_user,
      is_demo: true
    }));
    
    // Redirect to dashboard
    window.location.href = '/dashboard?demo=true';
    return null;
  }
  
  return null;
};

export default DemoPage;
